import React, { Fragment, useEffect, useState } from 'react'
import SelectField from 'components/Common/SelectField'
import Button from 'components/Common/Button'
import { MEETING_STEPPER, availabilityStatus, timeDurationAvailabilityOptions } from 'config'
import { getRoleFromPath, getUserRole } from 'utils/helper'
import { addDays } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { replaceTerm } from 'utils/helper'
import { BoAdvisorWrapper, MeetingDuration, MeetingDurationTitle } from 'components/Calendar/style'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import NoAssignedAdvisorsStep from './NoAssignedAdvisorsStep'
import redirectTo from 'utils/redirectTo'
import useTenantId from 'hooks/useTenantId'
import { useFlashParams } from '../../../hooks/useFlashParams'
import { isEmpty, sortBy, toNumber } from 'lodash'
import Tile from 'components/Common/Tile'
import { UserProfile } from '__generated__/api-types-and-hooks'

interface IBoAdvisorProps {
  onSelect: (schedulingUrl: string) => void
  userAvailabilityList
  setSelectedAdvisorId: (id: string) => void
  updateMeetingStepperAction: (params: { actionType: string; stepName: string }) => void
  getUserAvailabilityAction: (params: {
    role: string[]
    startTime: string
    endTime: string
    participantId: string
    timeZone: string
    duration: number
  }) => void
  userAvailabilityStatus: string
  boAdvisors: { firstName: string; lastName: string; id: string }[]
  userProfile: UserProfile
  selectedDuration: number
  setSelectedDuration: (duration: number) => void
  boAdvisorsLoading: boolean
  selectedAdvisorId: string
  getBoAdvisorAction: (payload?) => void
  getAdvisorProfileAction: (payload?) => void
  getUserAvailabilityHoursAction: (payload?) => void
  isAvailabilityHoursLoading: boolean
  userAvailabilityHoursList: { duration: string[] }
}

export default function BoAdvisor({
  onSelect,
  boAdvisors,
  boAdvisorsLoading,
  selectedAdvisorId,
  getBoAdvisorAction,
  userAvailabilityList,
  setSelectedAdvisorId,
  updateMeetingStepperAction,
  getUserAvailabilityAction,
  userAvailabilityStatus,
  getAdvisorProfileAction,
  userProfile,
  getUserAvailabilityHoursAction,
  isAvailabilityHoursLoading,
  userAvailabilityHoursList,
  setSelectedDuration,
  selectedDuration,
}: IBoAdvisorProps) {
  const tenantId = useTenantId()
  const userRole = getRoleFromPath()
  const currentDate = new Date()
  const startTime = currentDate.toISOString()
  const endTime = addDays(currentDate, 26).toISOString()
  const [selectedAdvisor, setSelectedAdvisor] = useState<any>(null)
  const { flashParams, removeFlashParams } = useFlashParams<{
    availabilityConnectionError: string
  }>()

  useEffect(() => {
    getBoAdvisorAction()
  }, [getBoAdvisorAction])

  useEffect(() => {
    removeFlashParams(['availabilityConnectionError'])
    setSelectedDuration(0)
    if (selectedAdvisorId) {
      getUserAvailabilityHoursAction({ selectedAdvisorId })
      getAdvisorProfileAction({ advisorId: selectedAdvisorId })
    }
    // eslint-disable-next-line
  }, [getUserAvailabilityAction, selectedAdvisorId, userRole, getAdvisorProfileAction])

  const handleSelect = (value) => {
    onSelect(value.schedulingUrl)
    setSelectedAdvisor(value)
    setSelectedAdvisorId(value.id)
  }

  const handleChange = (duration: string) => {
    if (toNumber(duration) !== selectedDuration) {
      setSelectedDuration(toNumber(duration))
      getUserAvailabilityAction({
        role: [getUserRole()],
        startTime,
        endTime,
        participantId: selectedAdvisorId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration: toNumber(duration),
      })
    }
  }

  const bsoProfile = useSelector((state: RootState) => state.bso.bsoProfile)
  let advisorTerm = replaceTerm('advisor', bsoProfile.terminology)?.toLocaleLowerCase()
  advisorTerm = advisorTerm.charAt(0).toUpperCase() + advisorTerm.slice(1)
  if (boAdvisorsLoading) {
    return (
      <BoAdvisorWrapper>
        <div className="h-full flex flex-col items-center p-10">
          <GradientCircleNotchIcon width="100" height="100" />
          <p className="font-bold mt-2">Loading schedules</p>
        </div>
      </BoAdvisorWrapper>
    )
  }
  if (!boAdvisorsLoading && !boAdvisors?.length) {
    return <NoAssignedAdvisorsStep />
  }

  const isError =
    selectedAdvisor &&
    userAvailabilityStatus === availabilityStatus.FAILED &&
    !isEmpty(selectedDuration.toString())

  const availabilityErrorMessage = flashParams.availabilityConnectionError
    ? `Your ${advisorTerm}’s calendar is having connection errors.
  Please message them to schedule a meeting.`
    : `Your ${advisorTerm}'s availability has not been set. Please message them to schedule a
  meeting.`

  const availabilityDurations = userAvailabilityHoursList?.duration
    ? sortBy(userAvailabilityHoursList.duration, (num) => parseInt(num))
    : []

  const durationOptions = !isEmpty(availabilityDurations)
    ? availabilityDurations.map((duration) => ({
        label: `${duration} min.`,
        value: duration,
      }))
    : timeDurationAvailabilityOptions

  return (
    <BoAdvisorWrapper>
      <SelectField
        label={advisorTerm}
        name="advisors"
        placeholder="Select"
        options={boAdvisors
          ?.sort((a, b) => a.lastName.localeCompare(b.lastName))
          .map((user) => {
            return { label: `${user.firstName} ${user.lastName}`, value: user }
          })}
        onChange={handleSelect}
        value={selectedAdvisor}
      />
      <MeetingDuration>
        {isAvailabilityHoursLoading && (
          <div className="h-full flex flex-col items-center">
            <GradientCircleNotchIcon width="80" height="80" />
          </div>
        )}
        {selectedAdvisor && !isAvailabilityHoursLoading && (
          <Fragment>
            <MeetingDurationTitle>Select meeting duration:</MeetingDurationTitle>
            <div className="flex flex-row gap-3 flex-wrap">
              {durationOptions.map((option) => (
                <Tile
                  onChange={handleChange}
                  value={selectedDuration.toString()}
                  label={option.label}
                  optionValue={option.value}
                  className={'w-[127px] !h-[70px]'}
                />
              ))}
            </div>
          </Fragment>
        )}
      </MeetingDuration>
      {selectedAdvisor &&
        userAvailabilityStatus === availabilityStatus.SUCCESS &&
        !isAvailabilityHoursLoading &&
        !isEmpty(userAvailabilityList?.availability) && (
          <Button
            label="Select a Date"
            onClick={() =>
              updateMeetingStepperAction({
                actionType: 'increment',
                stepName: MEETING_STEPPER.CALENDAR,
              })
            }
            disabled={isEmpty(userAvailabilityList?.availability)}
            className="w-full"
          />
        )}
      {selectedDuration.toString() && userAvailabilityStatus === availabilityStatus.LOADING && (
        <div className="h-full flex flex-col items-center p-5">
          <GradientCircleNotchIcon width="30" height="30" />
        </div>
      )}
      {isError && (
        <Fragment>
          <p>{availabilityErrorMessage}</p>
          <Button
            className="mt-8 w-full"
            variant="primary"
            label="Send a message"
            onClick={() => {
              redirectTo(`/owner/${tenantId}/chat`)
            }}
          />
        </Fragment>
      )}
    </BoAdvisorWrapper>
  )
}

BoAdvisor.defaultProps = {
  list: [],
  onSelect: () => {},
  boAdvisors: [],
  selectedAdvisorId: '',
  userAvailabilityList: [],
  setSelectedAdvisorId: () => {},
  updateMeetingStepperAction: () => {},
  getUserAvailabilityAction: () => {},
  userAvailabilityStatus: '',
  userProfile: {},
}
